'use client';
import { FC, ReactNode } from 'react';
import ImagesLoading from '@/features/ImagesLoading/ImagesLoading';
import { ImagesEmpty } from '@/features/ImagesEmpty/ImagesEmpty';
import PreviewImage from '@/features/PreviewImage/PreviewImage';
import { motion } from 'framer-motion';
import { cva } from 'class-variance-authority';
import { IImage } from '@/shared/api/types/image';
import { Gallery } from '@/shared/ui/Gallery/Gallery';
import clsx from 'clsx';
import Container from '@/shared/ui/Container/ui/Container';

interface Props {
  className?: string;
  isLoading?: boolean;
  isFavoriteIconAfter?: boolean;
  firstItem?: ReactNode;
  images?: IImage[];
  hrefSelector?: (image: IImage) => string;
  hideAvatar?: boolean;
  onLoadMore?: () => void;
  empty?: {
    title: string;
    subtitle?: string;
  };
}

const PreviewImageItems: FC<Props> = ({
  className,
  images = [],
  empty,
  isFavoriteIconAfter,
  firstItem,
  isLoading,
  hrefSelector,
  hideAvatar,
  onLoadMore,
}) => {
  if (isLoading)
    return (
      <Container
        className={clsx(cvaRoot(), className)}
        size={'xl'}
        gutters={'sm'}>
        <ImagesLoading />
      </Container>
    );

  if (!images.length && !firstItem)
    return (
      <Container
        className={clsx(cvaRoot(), className)}
        size={'xl'}
        gutters={'sm'}>
        <ImagesEmpty {...empty} />
      </Container>
    );

  return (
    <Container
      className={clsx(cvaRoot(), className)}
      size={'xl'}
      gutters={'sm'}>
      <Gallery>
        {firstItem}
        {images.map((el) => (
          <PreviewImage
            key={el.photoId}
            item={el}
            hrefSelector={hrefSelector}
            isFavoriteIconAfter={isFavoriteIconAfter}
            hideAvatar={hideAvatar}
          />
        ))}

        <motion.div
          key={images.length}
          className={cvaLoadMoreTrigger()}
          onViewportEnter={onLoadMore}
        />
      </Gallery>
    </Container>
  );
};

const cvaRoot = cva(['ImagesList-cvaRoot']);

const cvaLoadMoreTrigger = cva([
  'ImagesList-cvaLoadMore',
  'absolute -z-[9999] bottom-[136rem] left-1/2 -translate-x-1/2',
  'w-0 h-0',
  'invisible pointer-events-none',
]);

export default PreviewImageItems;
